/**
 * "Screen a deal" (DOP-615): pick the deal's Deal Room folder, or several,
 * and start. It replaces the "Add deals" side card.
 *
 * A deal is named after its folder, because the folder IS the deal almost
 * every time and a name box is one more thing to fill in. Renaming is in the
 * row's "…". A folder that is already a deal on this workflow is shown but
 * cannot be picked: the server holds one deal per folder, and offering it
 * would only buy a 409.
 *
 * A deal with no folder yet starts on /upload. Files become a Deal Room
 * folder there, and that folder is then listed here.
 *
 * The hold-out note is not decoration. The firm's finished memos sit in the
 * same folders, and a screener that reads the answer key is not being tested.
 */

import { ArrowRight, Info, Loader2, Upload, X } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";

import ModalDialog from "../ModalDialog";
import type {
	DealScreeningCreateDealPayload,
	DealScreeningFolderCandidate,
} from "../../types/dealScreening";
import { countLabel } from "./dealScreeningFormat";

export interface ScreenDealDialogProps {
	folders: DealScreeningFolderCandidate[];
	foldersLoading: boolean;
	foldersError: boolean;
	onRetryFolders: () => void;
	/** Folder ids already used by a deal on this workflow. */
	usedFolderIds: ReadonlySet<number>;
	/** Never rejects: resolves `{ok: false}` after reporting the failure. */
	onCreate: (payload: DealScreeningCreateDealPayload) => Promise<{ ok: boolean }>;
	onClose: () => void;
}

const TITLE_ID = "screen-deal-title";

const ScreenDealDialog: React.FC<ScreenDealDialogProps> = ({
	folders,
	foldersLoading,
	foldersError,
	onRetryFolders,
	usedFolderIds,
	onCreate,
	onClose,
}) => {
	const [selected, setSelected] = useState<number[]>([]);
	const [saving, setSaving] = useState(false);

	const toggle = (folderId: number) =>
		setSelected((current) =>
			current.includes(folderId)
				? current.filter((id) => id !== folderId)
				: [...current, folderId],
		);

	const start = async () => {
		if (selected.length === 0 || saving) return;
		setSaving(true);
		try {
			// One at a time, in the order they were picked. A refused create
			// stops the batch and keeps it and the rest picked, so nothing has
			// to be chosen again. The ones that landed are unpicked.
			for (const folderId of selected) {
				const folder = folders.find((f) => f.folder_id === folderId);
				if (!folder) continue;
				const result = await onCreate({
					name: folder.name,
					folder_id: folderId,
					run_now: true,
				});
				if (!result.ok) return;
				setSelected((current) => current.filter((id) => id !== folderId));
			}
			onClose();
		} finally {
			setSaving(false);
		}
	};

	const available = folders.filter((folder) => !usedFolderIds.has(folder.folder_id));
	const taken = folders.filter((folder) => usedFolderIds.has(folder.folder_id));

	return (
		<ModalDialog titleId={TITLE_ID} onClose={onClose} size="xl">
			<div className="flex items-start gap-3">
				<div className="min-w-0 flex-1">
					<h2 id={TITLE_ID} className="text-[22px] font-semibold tracking-tight text-[#111111]">
						Screen a deal
					</h2>
					<p className="mt-1.5 text-base text-[#5B6472]">
						Pick the deal's files. Doppler reads them and writes a first-look memo,
						with every sentence linked to the page it came from.
					</p>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close"
					className="rounded-lg p-1.5 text-text-muted hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782]"
				>
					<X className="h-[18px] w-[18px]" aria-hidden />
				</button>
			</div>

			<fieldset className="mt-5 min-w-0">
				<legend className="text-xs font-semibold uppercase tracking-[.06em] text-[#6B7280]">
					Which deal?
				</legend>
				<div className="mt-2.5 grid max-h-[340px] gap-2.5 overflow-y-auto sm:grid-cols-2">
					{foldersLoading && folders.length === 0 ? (
						<p className="flex items-center gap-2 px-1 py-3 text-sm text-text-muted">
							<Loader2 className="h-4 w-4 animate-spin" aria-hidden />
							Loading your Deal Room folders…
						</p>
					) : foldersError && folders.length === 0 ? (
						<div className="px-1 py-3">
							<p className="text-sm text-text-muted">Couldn't load your folders.</p>
							<button
								type="button"
								onClick={onRetryFolders}
								className="mt-1 text-sm font-medium text-[#004C4B] hover:underline"
							>
								Try again
							</button>
						</div>
					) : (
						<>
							{[...available, ...taken].map((folder) => {
								const used = usedFolderIds.has(folder.folder_id);
								const checked = selected.includes(folder.folder_id);
								return (
									<label
										key={folder.folder_id}
										className={`flex items-center gap-3 rounded-[10px] px-3.5 py-3 ${
											checked
												? "border-2 border-[#004C4B] bg-[#f6fbfb] px-[13px] py-[11px]"
												: "border border-gray-200 bg-white"
										} ${used ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-gray-50"}`}
									>
										<input
											type="checkbox"
											name="deal-folders"
											className="h-4 w-4 rounded border-gray-300 text-[#004C4B] focus:ring-[#008782]"
											checked={checked}
											disabled={used || saving}
											onChange={() => toggle(folder.folder_id)}
										/>
										<span className="min-w-0 flex-1">
											<span className="block truncate text-base font-semibold text-[#111111]">
												{folder.name}
											</span>
											<span className="mt-0.5 block text-xs text-[#5B6472]">
												{used ? "Already screened here" : countLabel(folder.file_count, "file")}
											</span>
										</span>
									</label>
								);
							})}
							<Link
								to="/upload"
								className="flex items-center gap-3 rounded-[10px] border-[1.5px] border-dashed border-[#99CFCD] bg-white px-3.5 py-3 hover:bg-[#f6fbfb]"
							>
								<Upload className="h-[18px] w-[18px] shrink-0 text-[#004C4B]" aria-hidden />
								<span className="min-w-0 flex-1">
									<span className="block text-base font-semibold text-[#004C4B]">
										A new deal: upload its files
									</span>
									<span className="mt-0.5 block text-xs text-[#5B6472]">
										Drop a folder, PDFs, Excel or a ZIP
									</span>
								</span>
							</Link>
						</>
					)}
				</div>
			</fieldset>

			<p className="mt-4 flex gap-2.5 rounded-[10px] bg-[#f7f7f6] px-3.5 py-3 text-sm leading-relaxed text-[#3f4247]">
				<Info className="mt-0.5 h-4 w-4 shrink-0 text-[#004C4B]" aria-hidden />
				<span>
					Keep the company's own deal screen or finished memo out of the folder.
					The new memo is built from the source files, not from it.
				</span>
			</p>

			<div className="mt-6 flex items-center gap-2.5">
				<span className="text-sm text-[#5B6472]">
					You can leave while it works. The deal list shows when it's ready.
				</span>
				<button
					type="button"
					onClick={onClose}
					className="ml-auto h-10 shrink-0 rounded-lg border border-gray-200 bg-white px-[18px] text-base font-medium text-text-strong hover:bg-gray-50"
				>
					Cancel
				</button>
				<button
					type="button"
					onClick={() => void start()}
					disabled={selected.length === 0 || saving}
					className="inline-flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-lg bg-[#004C4B] px-[18px] text-base font-semibold text-white hover:bg-[#003a3a] disabled:opacity-50"
				>
					{saving ? (
						<Loader2 className="h-4 w-4 animate-spin" aria-hidden />
					) : null}
					{selected.length > 1 ? `Start screening ${selected.length} deals` : "Start screening"}
					{!saving && <ArrowRight className="h-4 w-4" aria-hidden />}
				</button>
			</div>
		</ModalDialog>
	);
};

export default ScreenDealDialog;
